import React, { useState, useEffect, useRef } from 'react'
import { 
  X, 
  ScrollText, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  ExternalLink,
  FileText,
  AlertCircle
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Props for the TermsOfServiceModal component
 * Controls modal visibility and callbacks for user actions
 */
interface TermsOfServiceModalProps {
  isOpen: boolean
  onAccept: () => void
  onDecline: () => void
}

/**
 * TermsOfServiceModal Component
 * 
 * A modal dialog that prompts users to accept the Terms of Service
 * before connecting their wallet. This is a legal requirement that ensures
 * users acknowledge the risks and terms of using the platform.
 * 
 * Features:
 * - Accessible modal with proper focus management (WCAG 2.1 compliant)
 * - Checkbox for explicit consent
 * - Link to full Terms of Service page
 * - Summary of key terms
 * - Accept/Decline buttons
 * - Escape key and backdrop click to close
 * - Focus trap within modal when open
 * - Mobile-first responsive design with proper z-index layering
 * - Uses React Portal to render above all other content (avoids stacking context issues)
 * 
 * Note: Uses standard <a> tags instead of React Router's <Link> because this
 * modal is rendered outside of the BrowserRouter context (via TermsProvider
 * which is positioned above the Router in the component tree).
 */
export function TermsOfServiceModal({
  isOpen,
  onAccept,
  onDecline
}: TermsOfServiceModalProps): JSX.Element | null {
  const [isChecked, setIsChecked] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const acceptButtonRef = useRef<HTMLButtonElement>(null)
  const checkboxRef = useRef<HTMLInputElement>(null)

  /**
   * Handle escape key to close modal and manage body scroll.
   * Prevents body scroll when modal is open to avoid background scrolling on mobile.
   * Uses overflow: hidden on documentElement for better iOS Safari support.
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onDecline()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      // Use both html and body for cross-browser mobile support (especially iOS Safari)
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      // Prevent iOS Safari bounce-scroll
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${window.scrollY}px`
      
      // Focus the checkbox when modal opens for accessibility
      setTimeout(() => {
        checkboxRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      // Restore body scroll
      const scrollY = document.body.style.top
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
      // Restore scroll position after removing fixed positioning
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
      }
    }
  }, [isOpen, onDecline])

  /**
   * Handle focus trap within modal.
   * Ensures keyboard navigation stays within the modal for accessibility.
   */
  useEffect(() => {
    if (!isOpen) return

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [isOpen])

  /**
   * Handle checkbox change.
   * Updates the checked state to enable/disable the Accept button.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked)
  }

  /**
   * Handle accept button click.
   * Only proceeds if terms checkbox is checked.
   */
  const handleAccept = () => {
    if (isChecked) {
      onAccept()
    }
  }

  /**
   * Handle backdrop click.
   * Closes modal when clicking outside the content area.
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onDecline()
    }
  }

  // Don't render if modal is not open
  if (!isOpen) return null

  return (
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center",
        "p-3 sm:p-4",
        // z-[10000] ensures the modal is above EVERYTHING:
        // - Layout header (z-50 inside z-10 stacking context)
        // - Solana wallet adapter modal (typically z-1050)
        // - Mobile menu backdrop (z-40 inside z-10 stacking context)  
        // - Toast notifications
        "z-[10000]"
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      aria-describedby="terms-modal-description"
    >
      {/* Backdrop overlay - covers entire viewport including notches */}
      <div 
        className="absolute inset-0 bg-dark-bg/90 backdrop-blur-sm"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Modal content container
       * - Uses explicit styles to avoid inheriting glass-card's will-change/transform
       *   which can break fixed positioning on mobile browsers
       * - max-h uses dvh (dynamic viewport height) for mobile browser toolbar awareness
       * - Falls back to vh for older browsers
       */}
      <div
        ref={modalRef}
        className={cn(
          "relative w-full max-w-2xl overflow-y-auto",
          "rounded-xl sm:rounded-2xl",
          "border border-dark-border",
          "animate-scale-in",
          // Mobile-optimized max height using dynamic viewport units with fallback
          "max-h-[85vh] sm:max-h-[90vh]"
        )}
        style={{
          // Explicit background and border instead of glass-card class
          // to avoid will-change: transform creating a new stacking context
          // that breaks fixed positioning on mobile browsers
          background: 'rgba(12, 16, 28, 0.98)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 224, 255, 0.15)',
          // Support for dynamic viewport height on modern mobile browsers
          maxHeight: 'min(85dvh, 85vh)',
        }}
      >
        {/* Header - sticky within modal scroll container */}
        <div className="sticky top-0 border-b border-dark-border p-4 sm:p-6 z-10"
          style={{
            background: 'rgba(12, 16, 28, 0.97)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyber-cyan/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <ScrollText className="w-5 h-5 sm:w-6 sm:h-6 text-cyber-cyan" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 
                  id="terms-modal-title" 
                  className="text-xl sm:text-2xl font-bold text-gradient-cyber truncate"
                >
                  Terms of Service
                </h2>
                <p className="text-text-muted text-xs sm:text-sm">
                  Please review before connecting your wallet
                </p>
              </div>
            </div>
            <button
              onClick={onDecline}
              className={cn(
                "p-2 rounded-lg flex-shrink-0",
                "border border-dark-border",
                "hover:bg-status-error/20 hover:border-status-error/50",
                "transition-colors duration-200",
                "min-w-[40px] min-h-[40px] flex items-center justify-center"
              )}
              aria-label="Close Terms of Service modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Scrollable content area */}
        <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto" id="terms-modal-description">
          {/* Important Notice */}
          <div className="rounded-xl border-l-4 border-l-status-warning border-y border-r border-status-warning/30 p-3 sm:p-4 bg-status-warning/5">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h3 className="font-bold text-status-warning mb-1 text-sm sm:text-base">Important Notice</h3>
                <p className="text-text-secondary text-xs sm:text-sm">
                  By connecting your wallet, you agree to the terms and conditions of using CTO Pools. 
                  Please read the key points below carefully.
                </p>
              </div>
            </div>
          </div>

          {/* Key Terms Summary */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary flex items-center space-x-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-cyan" aria-hidden="true" />
              <span>Key Terms Summary</span>
            </h3>
            
            <div className="grid gap-2.5 sm:gap-3">
              {/* Risk Acknowledgment */}
              <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-status-error flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">Risk Acknowledgment</h4>
                    <p className="text-text-secondary text-xs sm:text-sm">
                      Cryptocurrency investments are highly speculative. You may lose all deposited funds. 
                      CTO Pools is not responsible for any financial losses.
                    </p>
                  </div>
                </div>
              </div>

              {/* Non-Custodial Platform */}
              <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
                <div className="flex items-start space-x-3">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">Non-Custodial Platform</h4>
                    <p className="text-text-secondary text-xs sm:text-sm">
                      You retain full custody of your funds. All transactions occur directly on the 
                      Solana blockchain through smart contracts.
                    </p>
                  </div>
                </div>
              </div>

              {/* No Affiliation */}
              <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-yellow flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">No Third-Party Affiliation</h4>
                    <p className="text-text-secondary text-xs sm:text-sm">
                      CTO Pools is NOT affiliated with any token or project promoted on the platform. 
                      Always do your own research (DYOR) before participating.
                    </p>
                  </div>
                </div>
              </div>

              {/* Wallet Security */}
              <div className="rounded-xl border border-dark-border/50 p-3 sm:p-4 bg-dark-surface/30">
                <div className="flex items-start space-x-3">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyber-purple flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">Wallet Security</h4>
                    <p className="text-text-secondary text-xs sm:text-sm">
                      You are solely responsible for maintaining the security of your wallet and 
                      private keys. We cannot recover lost funds.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full Terms Link - Using <a> instead of <Link> since modal renders outside Router context */}
          <div className="text-center">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-cyber-cyan hover:text-cyber-cyan/80 transition-colors text-sm sm:text-base"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Read Full Terms of Service</span>
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>

          {/* Checkbox Agreement - touch-friendly sizing */}
          <div className="rounded-xl border border-cyber-cyan/30 p-3 sm:p-4 bg-cyber-cyan/5">
            <label className="flex items-start space-x-3 cursor-pointer group">
              <input
                ref={checkboxRef}
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className={cn(
                  // Larger touch target for mobile (min 44px effective area via padding)
                  "w-5 h-5 sm:w-5 sm:h-5 mt-0.5 rounded border-2 border-dark-border bg-dark-surface",
                  "checked:bg-cyber-cyan checked:border-cyber-cyan",
                  "focus:ring-2 focus:ring-cyber-cyan/50 focus:ring-offset-0",
                  "transition-all duration-200 cursor-pointer",
                  "flex-shrink-0"
                )}
                aria-describedby="terms-agreement-label"
              />
              <span id="terms-agreement-label" className="text-text-primary select-none group-hover:text-cyber-cyan transition-colors text-sm sm:text-base leading-relaxed">
                I have read and agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyber-cyan underline hover:text-cyber-cyan/80"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                {' '}and acknowledge the risks associated with cryptocurrency transactions.
              </span>
            </label>
          </div>
        </div>

        {/* Footer with action buttons - sticky at bottom for mobile accessibility */}
        <div className="sticky bottom-0 border-t border-dark-border p-4 sm:p-6 z-10"
          style={{
            background: 'rgba(12, 16, 28, 0.97)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {/* Stack buttons vertically on very small screens, horizontal on sm+ */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end">
            <button
              onClick={onDecline}
              className={cn(
                "px-6 py-3 rounded-xl text-sm font-medium",
                "border border-dark-border text-text-secondary",
                "hover:bg-dark-panel/50 hover:text-text-primary",
                "transition-all duration-200",
                // Minimum touch target size for mobile (WCAG 2.1)
                "min-h-[44px]"
              )}
              aria-label="Cancel and close modal"
            >
              Cancel
            </button>
            <button
              ref={acceptButtonRef}
              onClick={handleAccept}
              disabled={!isChecked}
              className={cn(
                "px-6 py-3 rounded-xl text-sm font-medium flex items-center justify-center space-x-2",
                "border transition-all duration-200",
                // Minimum touch target size for mobile (WCAG 2.1)
                "min-h-[44px]",
                isChecked
                  ? "bg-cyber-cyan/20 border-cyber-cyan text-cyber-cyan hover:bg-cyber-cyan/30 hover:shadow-neon-cyan"
                  : "bg-dark-surface/50 border-dark-border text-text-muted cursor-not-allowed opacity-50"
              )}
              aria-label={isChecked ? "Accept terms and connect wallet" : "Please check the checkbox to accept terms"}
            >
              <CheckCircle className="w-5 h-5" aria-hidden="true" />
              <span>Accept & Continue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfServiceModal
